<template>
  <div class="distribution-bg min-h-screen flex flex-col justify-between">
    <div class="flex-1 flex flex-col items-center justify-center">
      <div
        id="distribution-white-box"
        ref="whiteBoxRef"
        class="max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-24 bg-white bg-opacity-90 rounded-xl border border-gray-200 shadow-lg"
      >
        <div
          class="flex justify-between items-center mb-8 px-16"
          :class="isFullScreen ? 'px-0' : 'px-16'"
        >
          <h1 class="text-2xl font-bold text-gray-800 tracking-tight">
            Distribution
          </h1>
          <Button
            v-if="isFullScreen"
            @click.prevent="goFullScreen"
            class="px-5 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 font-medium flex items-center space-x-2 text-sm shadow border border-gray-200"
          >
            <i class="fas fa-expand"></i>
          </Button>
          <Button
            v-else
            @click.prevent="goFullScreen"
            class="px-5 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 font-medium flex items-center space-x-2 text-sm shadow border border-gray-200"
          >
            <i class="fas fa-compress"></i>
          </Button>
        </div>
        <div
          class="flex flex-col lg:flex-row gap-0 items-center justify-center"
        >
          <!-- Badge Preview -->
          <div v-if="!isFullScreen" class="w-full lg:w-1/2 flex justify-center">
            <div
              class="rounded-xl border border-gray-100 p-6 bg-white bg-opacity-80 flex flex-col items-center"
              style="min-width: 320px; max-width: 340px; min-height: 420px"
            >
              <div class="w-full flex flex-col items-center">
                <div
                  v-if="vpDetail"
                  class="animate-fadeIn flex flex-col justify-center items-center transition-all duration-300 hover:scale-105"
                >
                  <TemplatePreview
                    :templateImage="vpDetail.image"
                    :templateName="vpDetail.name"
                    :templateLocation="
                      vpDetail.type === 'Participation'
                        ? vpDetail?.location?.split(', ')[0] || 'Virtual'
                        : null
                    "
                    :templateStartDate="
                      vpDetail.type === 'Participation'
                        ? vpDetail.startDate
                        : null
                    "
                    :templateEndDate="
                      vpDetail.type === 'Participation'
                        ? vpDetail.endDate
                        : null
                    "
                    :templateThemeBgColor="
                      vpDetail.badgeTheme
                        ? vpDetail.badgeTheme.backgroundCardColor
                        : null
                    "
                    :templateThemeTextColor="
                      vpDetail.badgeTheme ? vpDetail.badgeTheme.textColor : null
                    "
                    :templateThemeEffect="
                      vpDetail.badgeTheme
                        ? vpDetail.badgeTheme.cardEffect
                        : null
                    "
                    :templateThemeTexture="
                      vpDetail.badgeTheme
                        ? vpDetail.badgeTheme.textureImageURL
                        : null
                    "
                    :templateThemeIsLogoBack="
                      vpDetail.badgeTheme
                        ? vpDetail.badgeTheme.isLogoBack
                        : null
                    "
                    :templateThemeShape="
                      vpDetail.badgeTheme ? vpDetail.badgeTheme.shape : null
                    "
                    :issuerName="
                      vpDetail.type === 'Community'
                        ? vpDetail.templateIssuer.name
                        : vpDetail.issuer.name
                    "
                    :issuerImage="
                      vpDetail.type === 'Community'
                        ? vpDetail.templateIssuer.profileImage
                        : vpDetail.issuer.profileImage
                    "
                    :formatSm="false"
                    :type="vpDetail.type"
                    :is3d="true"
                  />
                </div>
              </div>
            </div>
          </div>
          <!-- QR Code Section -->
          <div
            :class="
              isFullScreen
                ? 'w-full flex flex-col items-center justify-center'
                : 'w-full lg:w-1/2 flex flex-col items-center justify-center'
            "
          >
            <div v-if="loading" class="flex justify-center items-center py-12">
              <img
                :src="generationImg"
                alt="Génération en cours"
                :class="[
                  'generation-img',
                  isFullScreen ? 'fullscreen' : 'normal',
                ]"
                :style="
                  isFullScreen
                    ? 'width: 340px; height: 340px'
                    : 'width: 280px; height: 280px'
                "
              />
            </div>
            <div v-else class="flex flex-col items-center w-full">
              <div class="flex flex-col items-center mb-6">
                <div class="text-4xl font-bold text-gray-800 mb-1">
                  {{ scannedCount }}
                </div>
                <div
                  class="text-gray-500 text-base mb-4"
                  style="letter-spacing: 0.01em"
                >
                  Scanned badges
                </div>
                <div class="flex flex-col items-center p-5">
                  <div
                    :class="[
                      'bg-white rounded-xl shadow-lg flex items-center justify-center mb-4 qr-container',
                      isFullScreen ? 'p-16' : 'p-10',
                    ]"
                    :style="
                      isFullScreen
                        ? 'width: 420px; height: 420px'
                        : 'width: 280px; height: 280px'
                    "
                  >
                    <qrcode-vue
                      :value="autoLink"
                      :size="isFullScreen ? 340 : 180"
                      level="H"
                      render-as="svg"
                      class="qr-code"
                      :foreground="'#000000'"
                    />
                  </div>
                </div>
                <a
                  :href="autoLink"
                  target="_blank"
                  class="text-gray-600 hover:text-gray-800 text-xs break-all text-center transition-colors duration-200 px-3 py-1.5 rounded-lg mb-2"
                  style="font-family: 'Inter', sans-serif"
                >
                  {{ autoLink }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <the-footer></the-footer>
  </div>
</template>

<script>
import { ref, inject, onMounted, watch, onUnmounted } from 'vue';
import Cookies from 'js-cookie';
import { useRoute } from 'vue-router';
import QrcodeVue from 'qrcode.vue';
import TemplatePreview from '@/components/TemplatePreview.vue';
import TemplateRolePreview from '@/components/TemplateRolePreview.vue';
import TheFooter from '@/components/TheFooter.vue';
import generationImg from '@/../assets/images/generation.png';

export default {
  components: {
    QrcodeVue,
    TemplatePreview,
    TemplateRolePreview,
    TheFooter,
  },
  setup() {
    const utils = inject('utils');
    const badgeName = ref(null);
    const route = useRoute();
    const badge = ref(route.query.badge);
    const loading = ref(false);
    const autoLink = ref(null);
    const isTracking = ref(false);
    const vpDetail = ref(null);
    const scannedCount = ref(0);
    const whiteBoxRef = ref(null);
    const isFullScreen = ref(false);
    const badgeBot = new utils.BadgeBot(badge.value);
    const generateLinks = async () => {
      loading.value = true;
      try {
        const response = await badgeBot.generateLinks(1);
        autoLink.value = response[0].link;
        Cookies.set('autoLink', JSON.stringify(autoLink.value), {
          expires: 365,
        });
        // Démarrer le tracking automatiquement après génération du lien
        startTracking();
      } catch (error) {
        console.error('Error generating links:', error);
        return null;
      } finally {
        loading.value = false;
      }
    };

    const trackLink = async () => {
      console.log('trackLink');
      if (!autoLink.value || !isTracking.value) return;

      try {
        const response = await badgeBot.getLinkStatus(autoLink.value);

        if (response && response.status !== 'pending') {
          isTracking.value = false;
          incrementScannedCount();
          console.log('Badge scanné! Statut:', response.status);
          // Générer un nouveau lien après scan
          await generateLinks();
        } else {
          // Polling toutes les 5 secondes au lieu de 100 secondes
          setTimeout(trackLink, 2000);
        }
      } catch (error) {
        console.error('Error tracking link:', error);
        // En cas d'erreur, réessayer après 10 secondes
        if (isTracking.value) {
          setTimeout(trackLink, 2000);
        }
      }
    };

    const startTracking = () => {
      if (!isTracking.value && autoLink.value) {
        isTracking.value = true;
        console.log('Démarrage du tracking pour:', autoLink.value);
        trackLink();
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isTracking.value = false;
        console.log('Tracking arrêté - page cachée');
      } else {
        startTracking();
        console.log('Tracking redémarré - page visible');
      }
    };

    const handleFocus = () => {
      startTracking();
    };

    function loadScannedCount() {
      const allCounts = JSON.parse(
        localStorage.getItem('scannedCounts') || '{}'
      );
      scannedCount.value = allCounts[badge.value] || 0;
    }

    function incrementScannedCount() {
      const allCounts = JSON.parse(
        localStorage.getItem('scannedCounts') || '{}'
      );
      allCounts[badge.value] = (allCounts[badge.value] || 0) + 1;
      localStorage.setItem('scannedCounts', JSON.stringify(allCounts));
      scannedCount.value = allCounts[badge.value];
    }

    function handleFullscreenChange() {
      const el = document.getElementById('distribution-white-box');
      isFullScreen.value =
        !!document.fullscreenElement && document.fullscreenElement === el;
    }

    function goFullScreen() {
      const el = document.getElementById('distribution-white-box');
      if (!el || !document.body.contains(el)) {
        console.error('L’élément n’est pas connecté au DOM');
        return;
      }
      if (!isFullScreen.value) {
        if (el.requestFullscreen) {
          el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          el.webkitRequestFullscreen();
        } else if (el.msRequestFullscreen) {
          el.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    }

    onMounted(() => {
      const autoLinkObject = Cookies.get('autoLink');
      if (autoLinkObject) {
        try {
          autoLink.value = JSON.parse(autoLinkObject);
        } catch (error) {
          generateLinks();
        }
      } else {
        generateLinks();
      }

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      document.addEventListener('fullscreenchange', handleFullscreenChange);

      const apikeySaves = Cookies.get('apiKeys');
      if (apikeySaves) {
        const apikeySavesObject = JSON.parse(apikeySaves);
        const apikeySave = apikeySavesObject.find(
          (apikeySave) => apikeySave.apiKey === badge.value
        );
        if (apikeySave) {
          vpDetail.value = {
            ...apikeySave.data,
            type: apikeySave.data.badgeCategory,
          };
        }
      }

      loadScannedCount();
    });

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      isTracking.value = false;
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    });

    // Corriger le watch pour surveiller autoLink.value directement
    watch(
      () => autoLink.value,
      (newLink, oldLink) => {
        if (newLink && newLink !== oldLink) {
          console.log('Nouveau lien détecté, démarrage du tracking');
          startTracking();
        }
      }
    );

    return {
      badgeName,
      badge,
      loading,
      autoLink,
      vpDetail,
      TemplatePreview,
      isTracking,
      generationImg,
      scannedCount,
      whiteBoxRef,
      goFullScreen,
      isFullScreen,
    };
  },
};
</script>

<style scoped>
.distribution-bg {
  min-height: 100vh;
  background: url('/assets/images/background_image.webp') no-repeat;
  background-position: center;
  background-size: cover;
  background-color: #f7f8fa;
  display: flex;
  flex-direction: column;
}

.footer-bg {
  background: transparent;
}

.qr-code {
  width: 180px;
  height: 180px;
  max-width: 180px;
  border-radius: 18px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.08);
  background: #fff;
  padding: 0;
  margin: 0 auto;
  display: block;
}

.qr-container {
  position: relative;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.08);
  padding: 18px;
  min-width: 180px;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-check-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 18px;
  font-size: 3rem;
  color: #34d399;
  pointer-events: none;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out;
}

/* Responsive */
@media (max-width: 1023px) {
  .max-w-3xl {
    max-width: 100% !important;
  }
  .qr-code,
  .qr-container {
    width: 140px !important;
    min-width: 140px !important;
    height: 140px !important;
    min-height: 140px !important;
    max-width: 140px !important;
  }
}

@media (max-width: 640px) {
  .distribution-bg {
    padding: 0 !important;
  }
  .max-w-3xl {
    padding: 0 !important;
  }
  .footer-bg {
    padding: 2rem 0 0 0 !important;
  }
}

.generation-img {
  display: block;
  margin: 0 auto;
  border-radius: 1.5rem;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.08);
  background: #fff;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.generation-img.normal {
  width: 8rem;
  height: 8rem;
}
.generation-img.fullscreen {
  width: 20rem;
  height: 20rem;
}
</style>
